/**
 * Canonical partners permission strings, copied verbatim from
 * openapi/generated/route_policy.gen.go. The SPEC predates the ratified API
 * and named `partners.parties.read|write`, `partners.terms.manage` and
 * `partners.groups.manage`; none of those exist. The generated policy wins.
 *
 * The single most consequential correction: GET /parties/{partyId} requires
 * `partners.party.pii.read`, not a plain read grant — the detail aggregate
 * carries tax-registration PII and the platform audits the access.
 */
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ShieldX } from "lucide-react";
import { Card, CardContent } from "@erp/ui";

import { ModuleBoundary } from "@/features/apps/module-boundary";
import { useSession } from "@/lib/session/use-session";

export const PARTNERS_PERMISSIONS = {
  partyRead: "partners.party.read",
  partyPiiRead: "partners.party.pii.read",
  partyCreate: "partners.party.create",
  partyUpdate: "partners.party.update",
  partyArchive: "partners.party.archive",
  addressRead: "partners.party_address.read",
  addressCreate: "partners.party_address.create",
  addressUpdate: "partners.party_address.update",
  addressArchive: "partners.party_address.archive",
  contactRead: "partners.party_contact.read",
  contactCreate: "partners.party_contact.create",
  contactUpdate: "partners.party_contact.update",
  contactArchive: "partners.party_contact.archive",
  customerProfileRead: "partners.customer_profile.read",
  customerProfileUpdate: "partners.customer_profile.update",
  customerProfileArchive: "partners.customer_profile.archive",
  supplierProfileRead: "partners.supplier_profile.read",
  supplierProfileUpdate: "partners.supplier_profile.update",
  supplierProfileArchive: "partners.supplier_profile.archive",
  channelIdentityRead: "partners.channel_identity.read",
  customerGroupRead: "partners.customer_group.read",
  customerGroupCreate: "partners.customer_group.create",
  customerGroupUpdate: "partners.customer_group.update",
  customerGroupArchive: "partners.customer_group.archive",
  supplierGroupRead: "partners.supplier_group.read",
  supplierGroupCreate: "partners.supplier_group.create",
  supplierGroupUpdate: "partners.supplier_group.update",
  supplierGroupArchive: "partners.supplier_group.archive",
  paymentTermsRead: "partners.payment_terms.read",
  paymentTermsCreate: "partners.payment_terms.create",
  paymentTermsUpdate: "partners.payment_terms.update",
  paymentTermsArchive: "partners.payment_terms.archive",
} as const;

export type PartnersPermission = (typeof PARTNERS_PERMISSIONS)[keyof typeof PARTNERS_PERMISSIONS];

/** Direct-route authorization: denied pages never mount or issue queries. */
export function PartnersPermissionBoundary({
  permission,
  children,
}: {
  permission: PartnersPermission;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const { hasPermission, hasModule } = useSession();

  // The app comes before the grant, and in that order deliberately. If the
  // company has not installed this app, saying "you lack permission" sends
  // somebody to an administrator who cannot help — the fix is to install it,
  // not to be granted something. The server answers the same way round.
  if (!hasModule("partners")) return <ModuleBoundary app="partners">{children}</ModuleBoundary>;

  if (hasPermission(permission)) return <>{children}</>;

  return (
    <div className="flex min-h-[22rem] items-center justify-center p-4">
      <Card
        role="alert"
        aria-labelledby="partners-forbidden-title"
        className="w-full max-w-xl border-destructive/30 bg-card/95 shadow-sm"
        data-testid="partners-route-forbidden"
      >
        <CardContent className="flex gap-4 p-6 sm:p-8">
          <span className="flex size-11 shrink-0 items-center justify-center rounded-md border border-destructive/30 bg-destructive/10 text-destructive">
            <ShieldX className="size-5" aria-hidden="true" />
          </span>
          <div className="min-w-0 space-y-2">
            <p className="font-mono text-xs font-semibold tracking-[0.18em] text-destructive">
              {t("partners.permissions.code")}
            </p>
            <h1 id="partners-forbidden-title" className="text-xl font-semibold tracking-tight">
              {t("partners.permissions.title")}
            </h1>
            <p className="text-sm text-muted-foreground">{t("errors.forbidden", { permission })}</p>
            <code dir="ltr" className="block w-fit rounded border bg-muted px-2 py-1 text-xs">
              {permission}
            </code>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
